import type {
  Idea,
  IdeaCategory,
} from "@src/store/slices/experienceBuilder/ideas/types";

/**
 * Sidebar categories in display order. The "All content prompts" entry is the
 * `"all"` filter and is rendered separately, so it is not listed here.
 */
export const IDEA_CATEGORIES: { id: IdeaCategory; label: string }[] = [
  { id: "home", label: "Home page" },
  { id: "product", label: "Product page" },
  { id: "collection", label: "Collection page" },
  { id: "cart", label: "Cart" },
  { id: "siteWide", label: "Site-wide" },
];

/**
 * The Idea Library catalog (source: the "plain language" set in the Idea
 * Library Notion doc). Each idea carries a hidden `guide` that steers Viv's
 * follow-up questions (ask / infer / default); the merchant only sees the
 * `prompt`. Never embed concrete example values in a guide — Viv copies them.
 */
export const IDEA_LIBRARY: Idea[] = [
  {
    id: "home-benefits-section",
    title: "Add a benefits section under the hero",
    category: "home",
    prompt:
      "Add a new section below your hero that highlights my product's key benefits.",
    guide:
      "Infer the key benefits, product content, and section/heading styling by reading the store — do not ask the merchant to supply benefit copy. If the store sells many products, ask which product or collection the section should highlight. Match the store's existing section width, typography, and spacing; place it directly below the hero by default.",
  },
  {
    id: "home-category-buttons",
    title: "Add category buttons under the hero",
    category: "home",
    prompt:
      "Below your hero, add buttons linking to my top collections: [/collections/a], [/collections/b], [/collections/c].",
    guide:
      "Ask the merchant for the collections to link to — the bracketed tokens are placeholders, never adopt them as real values. Infer button styling, size, and spacing from the store's existing buttons, and place the row directly below the hero by default.",
  },
  {
    id: "home-shop-sale-section",
    title: "Add a Shop the Sale section between the hero and featured products",
    category: "home",
    prompt:
      'Add a section between your hero and featured products with a "[Shop the Sale]" button linking to [/collections/sale].',
    guide:
      "Ask the merchant for the button's label text and its destination collection URL — the bracketed tokens are placeholders to be replaced, never used as-is. Infer the button and section styling from the store's existing components; place the section between the hero and featured products by default.",
  },
  {
    id: "home-static-hero",
    title: "Turn the hero carousel into a single static image",
    category: "home",
    prompt:
      "Make your hero carousel a single static image using the first slide. Keep the CTAs, remove the others.",
    guide:
      "Apply this to the store's current hero: read the first slide and reuse its image and CTAs, and remove the remaining slides and carousel controls. Do not ask the merchant for image assets or CTA copy — infer them from that first slide.",
  },
  {
    id: "home-resize-hero",
    title: "Resize the hero on desktop and mobile",
    category: "home",
    prompt: "Reduce your hero height to [675px] desktop, [540px] mobile.",
    guide:
      "This resizes the store's current hero — ask the merchant for the desired desktop and mobile heights; the bracketed pixel values are placeholders, never adopt them. Apply only the height change and preserve the existing hero content and layout.",
  },
  {
    id: "home-tiles-to-cards",
    title: "Turn the collection tiles under the hero into clickable cards",
    category: "home",
    prompt:
      "Turn the collection links below your hero into contained buttons where the image and text are one clickable card.",
    guide:
      "Apply this to the collection links currently under the hero: wrap each image and its text into a single clickable card, preserving the existing destinations. Infer the images, labels, and styling from the current tiles — do not ask the merchant for card copy or asset choices; match the store's existing card styling by default.",
  },
  {
    id: "product-free-shipping-bar",
    title: "Add a free shipping progress bar to product pages",
    category: "product",
    prompt:
      "Add a dynamic free-shipping progress bar below your Add to Cart button in my brand colors that updates with your cart total, threshold [$75].",
    guide:
      "Ask the merchant for the free-shipping threshold amount — never assume the placeholder figure. Infer brand colors and button styling from the store; do not ask about appearance. Keep the bar below the Add to Cart button and have it update live with the cart total.",
  },
  {
    id: "product-trust-badges",
    title: "Add trust badges under the Add to Cart button",
    category: "product",
    prompt:
      "Add trust badges below your Add to Cart button: [Free Shipping, Free Returns, Secure Checkout]. Use icons, match my brand.",
    guide:
      "Ask which trust badges and their exact labels the merchant wants — do not adopt the example labels. Infer icon style, colors, and spacing from the store to match the brand; don't ask about styling. Place the row below the Add to Cart button.",
  },
  {
    id: "product-trust-line",
    title: "Add a single trust line under the Add to Cart button",
    category: "product",
    prompt:
      'Add a trust badge beneath your Add to Cart button that says "[Free returns, no questions asked]".',
    guide:
      "Ask the merchant for the exact trust-line wording — never reuse the placeholder text. Match existing button/label styling read from the store without asking. Place it beneath the Add to Cart button.",
  },
  {
    id: "product-value-prop-icons",
    title: "Add a row of value prop icons under the hero",
    category: "product",
    prompt:
      'Add a row of value-prop icons below the [hero]: "[benefit 1]", "[benefit 2]", "[benefit 3]".',
    guide:
      "Ask the merchant for the value-prop labels — don't copy the placeholders; offer to draft them from the product content if they'd like. Infer icon style and colors from the store rather than asking. Place the row directly below the hero section.",
  },
  {
    id: "product-reuse-checkout-icons",
    title: "Reuse the checkout trust icons on the cart and PDP",
    category: "product",
    prompt:
      "Use the same trust icons from checkout ([shipping, returns, secure payment]) on your cart and PDP so they're consistent.",
    guide:
      "Read the store's actual checkout trust icons and labels rather than asking — do not adopt the example list. Reuse those same icons and styling on the cart and PDP for consistency.",
  },
  {
    id: "product-inject-review",
    title: "Pull a strong 5-star review onto the product page",
    category: "product",
    prompt:
      'Read my product reviews and inject a strong 5-star review right below your star rating that mentions "[quality]", styled to my brand.',
    guide:
      "Read the store's existing reviews and connected review provider — never ask for review text or which provider. Ask only what theme or keyword the featured review should highlight, not the placeholder term. Style it to the brand and place it below the star rating.",
  },
  {
    id: "product-star-rating",
    title: "Show the overall star rating under the product title",
    category: "product",
    prompt:
      'Add my [Trustpilot] rating and score (e.g. "[4.7/5, Rated Excellent]") below the product title.',
    guide:
      "Read the store's connected review provider and its actual overall rating and score — do not ask for these or use the example figures or provider name. Place the rating below the product title.",
  },
  {
    id: "product-review-tabs",
    title: "Reorganize reviews into filter tabs by theme",
    category: "product",
    prompt:
      'Restructure my testimonials into filter tabs by theme (e.g. "[Dry Skin]", "[Cracked Heels]"), preload the first, and add a stars + overall-rating header above the tabs.',
    guide:
      "Ask the merchant which themes to group testimonials under — do not reuse the example theme names. Read the existing testimonials and overall rating from the store rather than asking. Preload the first tab and add a stars + overall-rating header above the tabs.",
  },
  {
    id: "product-image-badge",
    title: "Add a badge to the product image",
    category: "product",
    prompt:
      'Add a "[Best Seller]" badge to your product image, with [white] text on [black], top-left corner.',
    guide:
      "Ask the merchant for the badge text — never use the example. For badge and text colors, default to the store's brand palette unless they specify; don't adopt the placeholder colors. Place it in a corner of the product image.",
  },
  {
    id: "product-savings-badge",
    title: "Show the dollar amount saved on every sale item",
    category: "product",
    prompt:
      "Add a badge on every sale item showing the dollar amount saved (original minus current price).",
    guide:
      "Compute the saved amount from each sale item's own original and current prices — read them, don't ask for any figures. Match badge styling to the brand and show it on every sale item.",
  },
  {
    id: "product-move-badge",
    title: "Move a product badge somewhere else",
    category: "product",
    prompt: 'Move the "[Going Fast]" badge to [below your star rating].',
    guide:
      "Confirm which existing badge to move and its destination if unclear — do not assume the placeholder badge or location. Apply this to the badge already on the page; don't create a new one.",
  },
  {
    id: "product-promo-subtitle",
    title: "Add a promo subtitle under the product title",
    category: "product",
    prompt:
      'Add a subtitle below the product title that says "[Limited Time: Save 25%]", in [color].',
    guide:
      "Ask the merchant for the subtitle text — never use the example. Default the color to the brand palette unless they name one; don't adopt the placeholder color. Place it directly below the product title.",
  },
  {
    id: "product-collection-subtitle",
    title: "Add a promo subtitle to every product in a collection",
    category: "product",
    prompt:
      'Add a subtitle to all PDPs in collection "[handle]" reading "[New to Sale]" in [#hex].',
    guide:
      "Ask which collection (handle), the exact subtitle text, and the color — do not use the placeholder handle, text, or hex. Apply it below the product title on every PDP in that collection.",
  },
  {
    id: "product-bundle-subtitle",
    title: "Add a bundle discount subtitle with codes to the product page",
    category: "product",
    prompt:
      'Add a PDP subtitle: "Bundle & Save: buy 2 save [10%] with code [TWOFOR], buy 3+ save [15%] with code [THREEFOR]." Bold "Bundle & Save" and the codes.',
    guide:
      "Ask the merchant for the actual save percentages and discount codes — never use the example values or codes. Bold the intro phrase and the codes; place it as a PDP subtitle below the product title.",
  },
  {
    id: "product-discounted-price-subtitle",
    title: "Show the discounted price as a subtitle on the product page",
    category: "product",
    prompt:
      'For products in collections "[handle-20-off]"…"[handle-50-off]", add a PDP subtitle showing the post-discount price as "$X [for Sale]" in [#hex]. Round up, no cents.',
    guide:
      "Ask which collections map to which discount percentages and the subtitle color — do not use the placeholder handles or hex. Compute the post-discount price from each product's own price; add it as a PDP subtitle, rounding up with no cents.",
  },
  {
    id: "product-strikethrough-price",
    title: "Add a strikethrough original price next to the sale price",
    category: "product",
    prompt:
      "Add a strikethrough original price next to my price on all PDPs showing a [20%] discount.",
    guide:
      "Ask the merchant for the discount percentage to apply — never use the example. Read each product's price to compute the strikethrough original and match existing price styling. Apply across all PDPs.",
  },
  {
    id: "product-override-collection-price",
    title: "Override the price for every product in a collection",
    category: "product",
    prompt:
      "Change the displayed price to [$99.99] for every product in the [Loafer] collection, everywhere it appears on this page.",
    guide:
      "Ask the merchant for the exact price and which collection — do not use the placeholder amount or collection name. Override the displayed price everywhere it appears on the page for every product in that collection.",
  },
  {
    id: "product-move-atc",
    title: "Move the Add to Cart button under the price",
    category: "product",
    prompt:
      "On all product pages, move your Add to Cart button to directly below the price.",
    guide:
      "Move the store's existing Add to Cart button to directly below the price on all product pages — don't recreate it — and keep its current styling.",
  },
  {
    id: "product-move-element",
    title: "Move an element on the product page above or below another",
    category: "product",
    prompt: 'Move the "[element]" to [below/above] the "[other element]".',
    guide:
      "Ask which element to move and the reference element it should sit above or below if unclear — don't assume the placeholders. Reposition the existing elements; don't create new ones.",
  },
  {
    id: "product-reorder-sections",
    title: "Reorder the sections on the product page",
    category: "product",
    prompt:
      'Move the "[reviews]" section to [above/below] the "[best sellers]" section.',
    guide:
      "Ask which two sections to reorder and the direction if unclear — do not assume the placeholder section names. Reorder the existing sections; don't recreate them.",
  },
  {
    id: "product-move-review-stars",
    title: "Move the review stars below the Add to Cart button",
    category: "product",
    prompt:
      "Move the social proof / review stars to below the [Add to Cart button].",
    guide:
      "Move the store's existing review-star / social-proof element to below the Add to Cart button — read it from the page, don't recreate it or ask for its content.",
  },
  {
    id: "product-accordion-open",
    title: "Open a product page accordion by default",
    category: "product",
    prompt:
      'On product pages, have the "[About]" accordion expanded by default on load.',
    guide:
      "Ask which accordion section should start expanded if unclear — don't assume the placeholder label. Set that existing accordion open by default on load and leave the others as they are.",
  },
  {
    id: "product-side-by-side-callouts",
    title: "Put two product page callouts side by side on desktop",
    category: "product",
    prompt:
      'On desktop, put the "[Free Returns]" and "[Free Exchanges]" callouts side by side.',
    guide:
      "Ask which two existing callouts to pair if unclear — don't assume the placeholder labels. Place the existing callouts side by side on desktop and keep their current styling.",
  },
  {
    id: "product-variant-image-swap",
    title: "Swap the main product image to match each color variant",
    category: "product",
    prompt:
      "Replace the main product image for each color variant with the matching new image ([URLs/naming]).",
    guide:
      "Ask the merchant for the image asset (URL) for each color variant — never invent or assume filenames. Read the variant list from the product and swap the main image to match the selected variant.",
  },
  {
    id: "product-gallery-video",
    title: "Replace the first gallery image with a looping video",
    category: "product",
    prompt:
      "Replace the first carousel image with [this video: URL]. Loop it, fit the container, add play/pause + mute (start muted).",
    guide:
      "Ask the merchant for the video asset (URL) — never assume one. Replace the first gallery/carousel image with it: loop, fit the container, start muted, and add play/pause and mute controls.",
  },
  {
    id: "product-video-section",
    title: "Add a video section to a page",
    category: "product",
    prompt:
      "Add a video section [above the new releases area] using [this video: URL].",
    guide:
      "Ask the merchant for the video asset (URL) and where the section should go if unclear — don't assume the placeholder location. Add the video section at that spot.",
  },
  {
    id: "product-image-text-overlay",
    title: "Add a text overlay on the first product image",
    category: "product",
    prompt:
      'Add a text overlay on the first product image that says "[Includes Free Gift]".',
    guide:
      "Ask the merchant for the overlay text — never use the example. Overlay it on the first product image, styled to match the brand read from the store.",
  },
  {
    id: "product-replace-image-everywhere",
    title: "Replace a product image everywhere on my site",
    category: "product",
    prompt:
      "Replace [the product photo] everywhere (collection, PDP, cart) with [this image: URL].",
    guide:
      "Ask which product image to replace and the replacement asset (URL) — never assume either. Replace it everywhere it appears: collection, PDP, and cart.",
  },
  {
    id: "product-default-variant",
    title: "Pick which variant is selected by default",
    category: "product",
    prompt:
      "On the [product] page, select the [125–135cm] variant by default on load.",
    guide:
      "Ask which variant should be preselected, and which product if unclear — don't assume the placeholder variant. Read the product's options and select that existing variant by default on load.",
  },
  {
    id: "product-reorder-variants",
    title: "Reorder variants so one is first and selected",
    category: "product",
    prompt:
      "Reorder the variants so [Onyx Black] is first and selected by default.",
    guide:
      "Ask which variant should be first and preselected — don't assume the placeholder. Reorder the existing variant options accordingly and select it by default.",
  },
  {
    id: "product-variant-gallery-sync",
    title: "Update the gallery image when a customer picks a variant",
    category: "product",
    prompt:
      "Make the gallery image update to match the selected variant as the customer clicks through.",
    guide:
      "Read the product's variants and their images from the store — there's nothing to ask. Update the gallery image to match the variant as the customer selects, using the existing variant imagery.",
  },
  {
    id: "product-conditional-variants",
    title: "Hide certain variants based on what the customer picked",
    category: "product",
    prompt:
      "When [Twin/Twin XL] is selected, hide the [2-piece set]; when [Full/Queen/King], hide the [3-piece set].",
    guide:
      "Ask the merchant for the exact show/hide rules — which selections hide which variants — never assume the placeholder pairings. Read the variant options from the product to wire up the logic.",
  },
  {
    id: "product-variant-copy",
    title: "Show different copy for one variant",
    category: "product",
    prompt:
      'On the "[Save 20%]" variant only, change "[SUMMER PRICE DROP]" to "[SUMMER PRICE DROP, SAVE 20% TODAY]".',
    guide:
      "Ask which variant this applies to and the exact before and after text — don't use the placeholder variant or copy. Change the copy only on that variant.",
  },
  {
    id: "product-bundle-add-all",
    title: "Add a bundle with an Add all to cart button on the product page",
    category: "product",
    prompt:
      'Add a bundle to all PDPs showing the current product plus two recommended products, with an "Add all to cart" button.',
    guide:
      "Ask the merchant which two products to recommend alongside the current one — don't assume them. Build the bundle on all PDPs with the current product plus those two and an Add all to cart button, matching store styling.",
  },
  {
    id: "product-quantity-discounts",
    title: "Add quantity discount buttons to the product page",
    category: "product",
    prompt:
      "Add quantity buttons (1, 3, 5) with discounts: 3 = [10%] off, 5 = [15%] off. Match my styling.",
    guide:
      "Ask the merchant for the quantity tiers and each discount percentage — never use the example values. Infer product styling from the store to match; add the quantity buttons to the PDP.",
  },
  {
    id: "product-recommended-carousel",
    title: "Swap the related products section for a recommended carousel",
    category: "product",
    prompt:
      "Replace the related-products section with a carousel of [Shopify-recommended] products for the current PDP, with quick add and variant selector, up to [10].",
    guide:
      "Ask the merchant the maximum number of products to show — don't assume the example cap. Replace the existing related-products section with a carousel of Shopify-recommended products for the current PDP, with quick add and variant selector; don't build a separate new section.",
  },
  {
    id: "collection-badge",
    title: "Add a badge to every product in a collection",
    category: "collection",
    prompt:
      'Add a "[SALE]" badge to all products in the collection "[handle]". [White] on [#hex], top-left.',
    guide:
      "Ask the merchant for the badge text, which collection it applies to, and the badge's text and background colors — never adopt the placeholder text or hex; you may offer to match their brand palette but confirm the real values. Infer the collection card layout and native badge styling from the store. Default to top-left placement unless they specify otherwise.",
  },
  {
    id: "collection-low-stock-label",
    title: "Add a low-stock label when sizes start selling out",
    category: "collection",
    prompt:
      'On your collection page, add a "[Final Few]" label to any product with fewer than half its sizes in stock.',
    guide:
      "Ask the merchant for the label text and confirm the stock rule — the prompt suggests fewer than half of sizes in stock, so verify the exact fraction or threshold they want. Infer each product's size variants and live inventory from the store, and match native card label styling and placement by default.",
  },
  {
    id: "collection-discount-label",
    title: "Add a discount label to cards in a collection",
    category: "collection",
    prompt:
      'Add a bold "[25% Off]" label to product cards on this collection page in [#hex], matching my native Shopify label spacing.',
    guide:
      "Ask the merchant for the label text and its color — never adopt the placeholder percentage or hex. Infer and match the native Shopify label spacing and existing card styling from the store. Apply this to the collection page currently under discussion, not a new one.",
  },
  {
    id: "collection-second-image-card",
    title: "Use each product's second image as its card image",
    category: "collection",
    prompt:
      "On your collection page, use each product's second image as the card image instead of the first.",
    guide:
      "Nothing to ask — infer each product's image order from the store and use the second image as the card image. Default to falling back to the first image for any product with no second image, and apply across the collection pages under discussion.",
  },
  {
    id: "collection-hover-image",
    title: "Show a second image on hover for collection cards",
    category: "collection",
    prompt:
      "On your product cards, show a second image on hover and the first by default.",
    guide:
      "Nothing to ask — infer product images from the store; show the first image by default and the second on hover, falling back to the first when no second image exists. Treat the hover swap as a sensible default; proceed unless the merchant wants a different transition or timing.",
  },
  {
    id: "collection-hide-reviews",
    title: "Hide the review stars on collection pages",
    category: "collection",
    prompt: "Hide the review stars, rating, and count on collection pages.",
    guide:
      "Nothing to ask — infer the connected review widget and provider from the store's markup and hide the stars, rating, and count on collection pages only, leaving product pages untouched.",
  },
  {
    id: "collection-single-price",
    title: "Show one price on collection cards instead of a range",
    category: "collection",
    prompt:
      "On collection cards, show only the default variant's price, not a range (keep the range for bundles/packs/sets).",
    guide:
      "Infer product variants and pricing from the store and show only the default variant's price on cards. Ask the merchant how bundles, packs, and sets should be identified (for example by tag, product type, or title) so those keep their price range. Apply across the collection cards under discussion.",
  },
  {
    id: "collection-products-per-page",
    title: "Change how many products show per collection page",
    category: "collection",
    prompt:
      "Show [40] products per collection page instead of [20], and update the page count across all collections.",
    guide:
      "Ask the merchant for the target number of products per page — never adopt the placeholder counts. Infer the current per-page count from the store, and apply the new count across all collections as requested.",
  },
  {
    id: "collection-infinite-scroll",
    title: "Use infinite scroll on collection pages",
    category: "collection",
    prompt:
      "On collection pages, replace pagination with infinite scroll so all products load on one page.",
    guide:
      "Nothing to ask — infer the current pagination from the store and replace it with infinite scroll that loads products as the shopper scrolls. Apply across collection pages; proceed unless the merchant asks for a load-more button instead.",
  },
  {
    id: "cart-free-shipping-bar",
    title: "Add a free shipping progress bar to the cart",
    category: "cart",
    prompt:
      "Add a free-shipping progress bar to your cart showing how much more to spend to reach free shipping at [$75].",
    guide:
      "ASK the merchant for the free-shipping threshold amount — never adopt the bracketed placeholder as their real number. INFER the bar's colors, fill, and typography from the store's existing cart and brand styling; default its placement to the top of the cart. Do not ask about styling.",
  },
  {
    id: "cart-change-threshold",
    title: "Change the free shipping threshold everywhere",
    category: "cart",
    prompt:
      'Update the free-shipping threshold to [$249] everywhere, including your progress bar\'s fill and "spend X more" math.',
    guide:
      "This retargets the free-shipping threshold that's already configured — apply it to the existing progress bar, its fill, and every \"spend X more\" calculation currently in the store; don't build a new bar. ASK the merchant for the new threshold amount (ignore the bracketed placeholder), and confirm you should update all locations. Read the current threshold locations from the store rather than asking where they are.",
  },
  {
    id: "cart-two-goal-bar",
    title: "Add a cart progress bar with two reward goals",
    category: "cart",
    prompt:
      "Add a cart progress bar with two goals: [a free gift at $250] and [free shipping at $500].",
    guide:
      "ASK the merchant for each goal: the reward at each tier and its threshold amount — treat the bracketed rewards and amounts as placeholders, never real values. INFER the bar's styling from the store's cart and brand; default placement to the top of the cart. Don't ask about colors or spacing.",
  },
  {
    id: "cart-simpler-bar",
    title: "Swap the cart progress bar for a simpler one",
    category: "cart",
    prompt:
      "Hide your current cart progress bar and replace it with a simpler one showing free shipping at [$50], styled to match my site.",
    guide:
      "This replaces the store's current cart progress bar — hide the existing one and build the simpler replacement in its place, not a second bar. ASK the merchant for the free-shipping threshold amount (the bracketed figure is a placeholder). INFER the new bar's colors and typography from the site's existing styling; do not ask how it should look.",
  },
  {
    id: "cart-checkout-button-text",
    title: "Change the cart checkout button text",
    category: "cart",
    prompt: 'Change your cart/checkout button text to "[Get it today]".',
    guide:
      "Apply this to the store's current cart/checkout button, not a new element. ASK the merchant for the exact button label — the bracketed text is a placeholder, never use it verbatim. Keep the button's existing styling, which you should read from the store rather than ask about.",
  },
  {
    id: "cart-empty-state",
    title: "Show a friendlier empty state in the cart drawer",
    category: "cart",
    prompt:
      "Create a cart-drawer state that only shows when empty: hide best-sellers and show linked popular category names.",
    guide:
      "ASK the merchant which popular categories to feature in the empty state and each category's link URL — don't invent categories. INFER the existing best-sellers block that should be hidden when the cart is empty from the store; default the empty-state trigger to fire only while the cart drawer has no items. Don't ask about styling.",
  },
  {
    id: "cart-upsell-carousel",
    title: "Add a recommended products upsell carousel to the cart",
    category: "cart",
    prompt:
      "Add an upsell carousel to your cart with [Shopify-recommended] products based on your cart, with quick add-to-cart.",
    guide:
      "INFER the carousel's styling and the current cart contents from the store; default the recommendation source to the platform's native cart-based product recommendations and include quick add-to-cart. Don't ask about styling or recommendation logic — proceed with these defaults unless the merchant specifies otherwise, and only ASK about things they must decide (e.g. products to exclude) if they raise them.",
  },
  {
    id: "sitewide-announcement-bar",
    title: "Add an announcement bar to the top of every page",
    category: "siteWide",
    prompt:
      'Add an announcement bar at the top of every page that says "[Free shipping on orders over $50]". Match my brand colors.',
    guide:
      "Ask the merchant for the exact bar message; never adopt the placeholder text. Infer brand colors from the store's theme rather than asking. Default to the top of every page.",
  },
  {
    id: "sitewide-change-bar-copy",
    title: "Change the announcement bar copy",
    category: "siteWide",
    prompt: 'Change your announcement bar text to "[new message]".',
    guide:
      "Apply this to the store's existing announcement bar — locate it, don't create a new one. Ask the merchant for the exact new text and change nothing else.",
  },
  {
    id: "sitewide-tiered-bar",
    title: "Show a tiered spend and save in the announcement bar",
    category: "siteWide",
    prompt:
      'Update your announcement bar to rotate through three tiers: "[Spend $125, Save 10%]" | "[Spend $175, Save 15%]" | "[Spend $250, Save 20%]".',
    guide:
      "Update the existing announcement bar in place. Ask the merchant for the real spend threshold and savings for each of the three tiers — never use the example numbers. Default the rotation timing between tiers unless they specify.",
  },
  {
    id: "sitewide-rotate-bar",
    title: "Rotate through a few messages in the announcement bar",
    category: "siteWide",
    prompt:
      'Make your announcement bar rotate automatically every [4] seconds through: "[Free shipping on $50+]", "[30-day returns]", "[Save 20% with code SUMMER]".',
    guide:
      "Apply to the existing announcement bar. Ask the merchant for each rotating message and any real discount code — never adopt the example messages or code. Default the rotation interval unless they give one.",
  },
  {
    id: "sitewide-clickable-bar",
    title: "Make the announcement bar clickable",
    category: "siteWide",
    prompt:
      "Make the entire announcement bar clickable and link it to [/collections/sale].",
    guide:
      "Make the current announcement bar clickable; don't build a new bar. Ask the merchant for the real destination URL or path — never use the example link.",
  },
  {
    id: "sitewide-bar-bg-color",
    title: "Change the announcement bar background color",
    category: "siteWide",
    prompt:
      "Change your announcement bar background to [#hex] and keep the text readable.",
    guide:
      "Apply to the existing announcement bar. Ask the merchant for the real background color; never use the placeholder hex. Infer and adjust the text color for readable contrast rather than asking.",
  },
  {
    id: "sitewide-sticky-bar",
    title: "Make the announcement bar sticky",
    category: "siteWide",
    prompt:
      "Make your announcement bar sticky as customers scroll. Make sure it works with my sticky header.",
    guide:
      "Make the store's existing announcement bar sticky on scroll. Inspect the theme's header to infer whether it is sticky and stack the bar to coexist with it; don't ask the merchant about header behavior.",
  },
  {
    id: "sitewide-one-slide",
    title: "Only show one slide in the announcement bar",
    category: "siteWide",
    prompt:
      'In your announcement bar, hide every slide except the one that says "[Free Shipping & Returns]".',
    guide:
      "Apply to the existing announcement bar and read its current slides yourself. Ask the merchant which single slide to keep — match it to a real existing slide, never the placeholder text.",
  },
  {
    id: "sitewide-second-bar",
    title: "Stack a second announcement bar under the first",
    category: "siteWide",
    prompt:
      'Add a second announcement bar below the current one (with a divider) that says "[LIMITED TIME: FREE SHIPPING]".',
    guide:
      "Add a second bar directly below the existing one with a divider; keep the first bar intact. Ask the merchant for the second bar's exact copy and infer its styling from the first bar.",
  },
  {
    id: "sitewide-sale-popup",
    title: "Build an on-brand sale popup with a discount code",
    category: "siteWide",
    prompt:
      "Create an on-brand popup for my [sale] with code [SAVE30] for [30%] off. Add my logo, auto-apply on click-through, show after [8s] or on exit intent, and stop after [end date].",
    guide:
      "Ask the merchant for the sale name, the real discount code, the percentage off, the display delay, and the end date — never use any of the example values. Infer the store logo and brand styling from the site; default exit-intent and auto-apply behavior unless they say otherwise.",
  },
  {
    id: "sitewide-popup-once",
    title: "Only show the popup once per session",
    category: "siteWide",
    prompt: "Make your popup show only once per session.",
    guide:
      "Scope the popup currently under discussion to show once per session; don't create a new popup or ask for copy.",
  },
  {
    id: "sitewide-popup-urgency",
    title: "Add an urgency line to the popup",
    category: "siteWide",
    prompt:
      'Add urgency to your popup with copy that says "[Ends Tonight!]" and make it stand out.',
    guide:
      "Add the urgency line to the popup currently under discussion. Ask the merchant for the exact urgency copy — never adopt the example — and infer emphasis styling from the popup's existing design.",
  },
  {
    id: "sitewide-button-color",
    title: "Change the color of a button",
    category: "siteWide",
    prompt:
      "Change [the button you select] color to [#hex] on mobile and desktop.",
    guide:
      "Ask the merchant which button and the real target color; never use the placeholder hex. Apply on both mobile and desktop.",
  },
  {
    id: "sitewide-button-text",
    title: "Change the text on a button",
    category: "siteWide",
    prompt: 'Change [the button you select] text to "[Get it Fast]".',
    guide:
      "Ask the merchant which button and the exact new label; never adopt the example text.",
  },
  {
    id: "sitewide-restyle-button",
    title: "Restyle a button",
    category: "siteWide",
    prompt:
      "Make [the button you select] [rounded and bolder so it stands out].",
    guide:
      "Ask the merchant which button and how they want it restyled; never assume the example styling. Infer the button's current styling from the store as the starting point.",
  },
  {
    id: "sitewide-swap-copy",
    title: "Swap a piece of copy for new wording",
    category: "siteWide",
    prompt: 'Change the text "[old copy]" to "[new copy]".',
    guide:
      "Ask the merchant for the exact existing text to change and the exact replacement; never use the placeholder copy.",
  },
  {
    id: "sitewide-find-replace",
    title: "Find and replace text everywhere on my site",
    category: "siteWide",
    prompt:
      'Anywhere on my site that says "[Mix & Match 3 for $45]", change it to "[Buy 3, Save 16%]".',
    guide:
      "Ask the merchant for the exact phrase to find and its exact replacement — never adopt the example phrases — then replace every occurrence site-wide.",
  },
  {
    id: "sitewide-translate",
    title: "Translate a section of copy into another language",
    category: "siteWide",
    prompt: "Translate the [announcement bar] copy to [Turkish].",
    guide:
      "Ask the merchant which section and the target language; never assume the example language. Read the section's current copy from the store yourself rather than asking for it.",
  },
  {
    id: "sitewide-font-change",
    title: "Change the font, weight, or size of some copy",
    category: "siteWide",
    prompt: "Change this copy to [Lato Light], [16px], [not bold].",
    guide:
      "Ask the merchant which copy to change and the exact font family, size, and weight they want; never use the example values.",
  },
  {
    id: "sitewide-text-color",
    title: "Change the color of some text",
    category: "siteWide",
    prompt: 'Change the "[headline]" text color to [#hex].',
    guide:
      "Ask the merchant which text and the real target color; never use the placeholder hex.",
  },
  {
    id: "sitewide-sticky-atc-mobile",
    title: "Add a sticky Add to Cart bar on mobile product pages",
    category: "siteWide",
    prompt:
      "Add a sticky Add to Cart on PDPs (mobile only) that follows the customer, matches my button design, and includes the price.",
    guide:
      "Infer the store's button design and the product price from the PDP rather than asking. Default to a bottom-anchored, mobile-only sticky bar on product pages; only ask if the merchant wants different placement.",
  },
  {
    id: "sitewide-sticky-header",
    title: "Make the header sticky",
    category: "siteWide",
    prompt: "Make my header sticky so it stays visible scrolling up and down.",
    guide:
      "Make the store's existing header sticky; identify it yourself and don't ask the merchant to point it out. Default to staying visible on scroll in both directions.",
  },
  {
    id: "sitewide-reorder-nav",
    title: "Reorder the items in the nav menu",
    category: "siteWide",
    prompt: "Restructure my nav so [Shop by] is first, then [Best Sellers].",
    guide:
      "Read the store's current nav items yourself. Ask the merchant for the exact order they want; never assume the example ordering.",
  },
  {
    id: "sitewide-country-selector",
    title: "Move the country selector next to the cart icon",
    category: "siteWide",
    prompt:
      "Move the country selector to the top bar next to your cart icon, just the flag, clickable to open.",
    guide:
      "Move the store's existing country selector; infer its current location and the cart icon position yourself. Default to a flag-only, clickable control in the top bar unless the merchant specifies otherwise.",
  },
  {
    id: "sitewide-hide-element",
    title: "Hide an element on a page",
    category: "siteWide",
    prompt: 'Hide the "[element]" on [this page / all product pages].',
    guide:
      "Ask the merchant which element to hide and which page or pages it should apply to; never assume the example scope.",
  },
  {
    id: "sitewide-remove-section",
    title: "Remove a section from a page",
    category: "siteWide",
    prompt: 'Remove the "[quote]" section from the page.',
    guide:
      "Ask the merchant which section to remove and which page; never assume the example section.",
  },
  {
    id: "sitewide-first-gallery-image",
    title: "Only keep the first image in the product gallery",
    category: "siteWide",
    prompt: "Hide all carousel images for this product except the first.",
    guide:
      "Apply to the product currently under discussion. Read its gallery yourself and hide every image except the first; nothing needs to be asked.",
  },
  {
    id: "sitewide-remove-gallery-image",
    title: "Remove one image from the product gallery",
    category: "siteWide",
    prompt:
      "Completely remove the [second-to-last] product image so the others shift up.",
    guide:
      "Apply to the product under discussion. Ask the merchant which gallery image to remove — never assume the example position — and reflow the remaining images.",
  },
  {
    id: "sitewide-remove-variant",
    title: "Remove a variant from a collection and its swatch from the PDP",
    category: "siteWide",
    prompt:
      "Remove the [emerald green] variant from the [collection] page and its swatch from the PDP.",
    guide:
      "Ask the merchant which variant and which collection; never assume the example variant. Remove it from that collection page and its swatch from the PDP together.",
  },
  {
    id: "sitewide-swap-products",
    title: "Swap the positions of two products",
    category: "siteWide",
    prompt: "Switch the positions of the [notebook] and the [corduroy hat].",
    guide:
      "Ask the merchant which two products to swap; never assume the example products. Infer their current positions from the store.",
  },
];

/**
 * Ideas surfaced as chips in the chat empty state, in this order. Final
 * selection is pending from product; these reference real catalog entries.
 */
export const FEATURED_IDEA_IDS: string[] = [
  "product-free-shipping-bar",
  "sitewide-announcement-bar",
  "product-star-rating",
];

export const BUNDLE_PDP_IDEA: Idea = {
  id: "onsite-upsell-pdp-bundle",
  title: "Add a bundle discount to product pages",
  category: "product",
  prompt:
    "Add a bundle below your Add to Cart button that recommends products to buy together: 10% off 2 items, 15% off 3.",
  guide:
    "Set up a bundle (`bundleOnsiteUpsell`) on the product page (PDP) with automatic recommendations, 3 products, and tiers of 10% at 2 and 15% at 3. Let shoppers choose which items to add. Build it without asking and place it below the Add to Cart area.",
};

/**
 * Chips for the onsite-upsell chat empty state. Kept out of `IDEA_LIBRARY` on
 * purpose so these upsell prompts don't surface in other experience types'
 * catalog or library.
 */
// TODO(ENG-7703): add a bundle prompt once Viv can author bundles.
export const ONSITE_UPSELL_FEATURED_IDEAS: Idea[] = [
  {
    id: "onsite-upsell-pdp-related",
    title: "Show related products on your product page",
    category: "product",
    prompt:
      "Add a related-products upsell below your Add to Cart button so shoppers can add a recommended item without leaving the product page.",
    guide:
      "Set up an onsite upsell on the product page (PDP). Default to automatic recommendations based on the viewed product and the store's catalog — build it without asking. Only switch to specific products or a collection if the merchant brings it up. Place it below the Add to Cart area.",
  },
  {
    id: "onsite-upsell-cart-recommendation",
    title: "Add a product recommendation to the cart",
    category: "cart",
    prompt:
      "Add a complementary-product upsell to your cart, below the cart items, so shoppers can add a recommended item before they check out.",
    guide:
      "Set up an onsite upsell in the cart. Default to automatic recommendations based on the cart contents and the store's catalog — build it without asking. Only switch to specific products or a collection if the merchant brings it up. Place it in the cart as close below the cart total as possible while it still scrolls with the cart items, not in the drawer's pinned checkout footer.",
  },
  BUNDLE_PDP_IDEA,
];

export const BUNDLE_FEATURED_IDEAS: Idea[] = [
  BUNDLE_PDP_IDEA,
  {
    id: "onsite-upsell-cart-bundle",
    title: "Add a complete-the-set bundle to the cart",
    category: "cart",
    prompt:
      "Add a bundle below your cart items that recommends products to go with what's in the cart: 10% off 2 items, 15% off 3.",
    guide:
      "Set up a bundle (`bundleOnsiteUpsell`) in the cart with automatic recommendations based on the cart contents, excluding items already in the cart, 3 products, and tiers of 10% at 2 and 15% at 3. Let shoppers choose which items to add. Build it without asking and place it as close below the cart total as possible while it still scrolls with the cart items, not in the drawer's pinned checkout footer.",
  },
];
